import {NgModule} from '@angular/core';
import {RouterModule, Routes, UrlSegment} from '@angular/router';
import {ServicesComponent} from './services/services.component';
import {MsalGuard} from '@azure/msal-angular';
import {ManageServiceComponent} from './services/manage-service/manage-service.component';
import {UsersComponent} from './users/users.component';
import {CustomersComponent} from './users/customers/customers.component';
import {CustomerManagementComponent} from './users/customers/customer-management/customer-management.component';
import {BackofficesComponent} from './users/backoffices/backoffices.component';
import {BackofficeManagementComponent} from './users/backoffices/backoffice-management/backoffice-management.component';
import {MyProfileComponent} from './_shared/my-profile/my-profile.component';
import {EwalletViewComponent} from './ewallet/ewallet-view/ewallet-view.component';
import {CashierTransactionComponent} from './ewallet/transactions/cashier-transaction/cashier-transaction.component';
import {RoleGuard} from './_shared/role-guard';
import {PermissionGuard} from './_shared/permission-guard';
import {ServiceEditGuard} from './services/service-edit.guard';
import {AppRoles, PermissionKeys} from './app.constants';
import {ViewServiceComponent} from './services/view-service/view-service.component';
import {CguComponent} from './cgu/cgu.component';
import {TransactionsComponent} from './transactions/transactions.component';
import {CardsComponent} from './cards/cards.component';
import {AlarmComponent} from './alarms/alarm.component';
import {AlarmManagementComponent} from './alarms/alarm-management/alarm-management.component';
import {AlarmHistoryComponent} from './alarms/alarm-history/alarm-history.component';
import {AlarmDeviceManagementComponent} from './alarms/alarm-device-management/alarm-device-management.component';
import {AlertComponent} from './alerts/alert.component';
import {AlertHistoryComponent} from './alerts/alert-history/alert-history.component';
import {AlertDeviceManagementComponent} from './alerts/alert-device-management/alert-device-management.component';
import {AlertManagementComponent} from './alerts/alert-management/alert-management.component';
import {OfficeHourManagementComponent} from './infrastructure/office-hour-management/office-hour-management.component';
import {InfrastructureComponent} from './infrastructure/infrastructure.component';
import {CraningBookingPrintComponent} from './services/view-service/craning/craning-booking/craning-booking-print/craning-booking-print.component';
import {TransactionBillingComponent} from './transactions/transaction-billing/transaction-billing.component';
import {TransactionCommonComponent} from './transactions/transaction-common/transaction-common.component';
import {TransactionType} from './_services/configuration-services';
import {MaintenanceComponent} from './infrastructure/maintenance/maintenance.component';
import {ManageEquipmentComponent} from './infrastructure/maintenance/manage-equipment/manage-equipment.component';
import {InterventionsComponent} from './infrastructure/maintenance/interventions/interventions.component';
import {InformationsComponent} from './communication-channel/informations/informations.component';
import {SurveysComponent} from './communication-channel/surveys/surveys.component';
import {ContactComponent} from './communication-channel/contact/contact.component';
import {CommunicationChannelComponent} from './communication-channel/communication-channel.component';
import {CommunicationChannelGuard} from './communication-channel/utils/communication-channel.guard';
import {ContactManagementComponent} from './communication-channel/contact/contact-management/contact-management.component';
import {InformationsManagementComponent} from './communication-channel/informations/informations-management/informations-management.component';
import {SurveysManagementComponent} from './communication-channel/surveys/surveys-management/surveys-management.component';
import {ViewInfoComponent} from './communication-channel/informations/view-info/view-info.component';
import {ManageSurveyComponent} from './communication-channel/surveys/surveys-edition/manage-survey.component';
import {ManageInformationComponent} from './communication-channel/informations/manage-information/manage-information.component';
import {ViewSurveyComponent} from './communication-channel/surveys/view-survey/view-survey.component';
import {ForgetMeComponent} from './forget-me/forget-me.component';
import {ManageInterventionComponent} from './infrastructure/maintenance/interventions/manage-intervention/manage-intervention.component';
import {EwalletsComponent} from "./ewallet/ewallets.component";
import {StatisticsComponent} from './statistics/statistics.component';
import {TransactionFullSearchComponent} from './transactions/transaction-full-search/transaction-full-search.component';
import {CgvComponent} from "./cgv/cgv.component";
import {EwalletListComponent} from './ewallet/ewallet-list/ewallet-list.component';
import {EwalletClosureScheduleComponent} from './ewallet/ewallet-closure-schedule/ewallet-closure-schedule.component';

const routes: Routes = [
    {
        path: '',
        redirectTo: '/services',
        pathMatch: 'full',
    },
    {
        path: 'alarms',
        redirectTo: '/alarms/alarm-management',
        pathMatch: 'full',
    },
    {
        path: 'alarms',
        component: AlarmComponent,
        children: [
            {
                path: 'alarm-management',
                component: AlarmManagementComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionAlertesAlarmes, PermissionKeys.configurationAlertesAlarmes]
                },
                canActivate: [
                    MsalGuard, PermissionGuard
                ]
            },
            {
                path: 'alarm-history',
                component: AlarmHistoryComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionAlertesAlarmes, PermissionKeys.configurationAlertesAlarmes]
                },
                canActivate: [
                    MsalGuard, PermissionGuard
                ]
            },
            {
                path: 'alarm-device-management',
                component: AlarmDeviceManagementComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionAlertesAlarmes, PermissionKeys.configurationAlertesAlarmes]
                },
                canActivate: [
                    MsalGuard, PermissionGuard
                ]
            }
        ],
        data: {
            allowedPermissions: [PermissionKeys.gestionAlertesAlarmes, PermissionKeys.configurationAlertesAlarmes]
        },
        canActivate: [
            MsalGuard, PermissionGuard
        ]
    },
    {
        path: 'alerts',
        redirectTo: '/alerts/alert-management',
        pathMatch: 'full',
    },
    {
        path: 'alerts',
        component: AlertComponent,
        children: [
            {
                path: 'alert-management',
                component: AlertManagementComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionAlertesAlarmes, PermissionKeys.configurationAlertesAlarmes]
                },
                canActivate: [
                    MsalGuard, PermissionGuard
                ]
            },
            {
                path: 'alert-history',
                component: AlertHistoryComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionAlertesAlarmes, PermissionKeys.configurationAlertesAlarmes]
                },
                canActivate: [
                    MsalGuard, PermissionGuard
                ]
            },
            {
                path: 'alert-device-management',
                component: AlertDeviceManagementComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionAlertesAlarmes, PermissionKeys.configurationAlertesAlarmes]
                },
                canActivate: [
                    MsalGuard, PermissionGuard
                ]
            }
        ],
        data: {
            allowedPermissions: [PermissionKeys.gestionAlertesAlarmes, PermissionKeys.configurationAlertesAlarmes]
        },
        canActivate: [
            MsalGuard, PermissionGuard
        ]
    },
    {
        path: 'cgu',
        component: CguComponent,
    },
    {
        path: 'cgv',
        component: CgvComponent,
    },
    {
        path: 'communication-channel',
        component: CommunicationChannelComponent,
        children: [
            {
                path: 'information',
                component: InformationsComponent,
                data: {
                    communicationChannelType: 'information'
                },
                canActivate: [
                    MsalGuard, RoleGuard, CommunicationChannelGuard
                ]
            }, {
                path: 'information/manage',
                component: InformationsManagementComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionCommunication],
                    communicationChannelType: 'information'
                },
                canActivate: [
                    MsalGuard, PermissionGuard, CommunicationChannelGuard
                ]
            },
            {
                path: 'information/manage/create',
                component: ManageInformationComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionCommunication],
                    communicationChannelType: 'information'
                },
                canActivate: [
                    MsalGuard, PermissionGuard, CommunicationChannelGuard
                ]
            },
            {
                path: 'information/manage/:id',
                component: ManageInformationComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionCommunication],
                    communicationChannelType: 'information'
                },
                canActivate: [
                    MsalGuard, PermissionGuard, CommunicationChannelGuard
                ]
            },
            {
                path: 'information/:id',
                component: ViewInfoComponent,
                data: {
                    communicationChannelType: 'information'
                },
                canActivate: [
                    MsalGuard, RoleGuard, CommunicationChannelGuard
                ]
            },
            {
                path: 'surveys',
                component: SurveysComponent,
                data: {
                    communicationChannelType: 'survey'
                },
                canActivate: [
                    MsalGuard, RoleGuard, CommunicationChannelGuard
                ]
            }, {
                path: 'surveys/manage',
                component: SurveysManagementComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionCommunication],
                    communicationChannelType: 'survey'
                },
                canActivate: [
                    MsalGuard, PermissionGuard, CommunicationChannelGuard
                ]
            },
            {
                path: 'surveys/manage/create',
                component: ManageSurveyComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionCommunication],
                    communicationChannelType: 'survey'
                },
                canActivate: [
                    MsalGuard, PermissionGuard, CommunicationChannelGuard
                ]
            },
            {
                path: 'surveys/manage/:id',
                component: ManageSurveyComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionCommunication],
                    communicationChannelType: 'survey'
                },
                canActivate: [
                    MsalGuard, PermissionGuard, CommunicationChannelGuard
                ]
            },
            {
                path: 'surveys/:id',
                component: ViewSurveyComponent,
                data: {
                    communicationChannelType: 'survey'
                },
                canActivate: [
                    MsalGuard, RoleGuard, CommunicationChannelGuard
                ]
            },
            {
                path: 'contact',
                component: ContactComponent,
                data: {
                    communicationChannelType: 'contact'
                },
                canActivate: [
                    MsalGuard, RoleGuard, CommunicationChannelGuard
                ]
            }, {
                path: 'contact/manage',
                component: ContactManagementComponent,
                data: {
                    communicationChannelType: 'contact',
                    allowedPermissions: [PermissionKeys.gestionCommunication]
                },
                canActivate: [
                    MsalGuard, PermissionGuard, CommunicationChannelGuard
                ]
            }],
        data: {
            communicationChannelType: 'general',
            allowedRoles: [AppRoles.backoffice, AppRoles.customer]
        },
        canActivate: [
            MsalGuard, RoleGuard, CommunicationChannelGuard
        ]
    },
    {
        path: 'ewallet',
        component: EwalletViewComponent,
        data: {
            allowedRoles: [AppRoles.customer]
        },
        canActivate: [
            MsalGuard, RoleGuard
        ],
    },
    {
        path: 'ewallet/:customerId',
        component: EwalletViewComponent,
        data: {
            allowedPermissions: [PermissionKeys.gestionTransactions]
        },
        canActivate: [
            MsalGuard, PermissionGuard
        ],
    },
    {
        path: 'ewallet/:customerId/cashier-operation',
        component: CashierTransactionComponent,
        data: {
            allowedPermissions: [PermissionKeys.gestionTransactions]
        },
        canActivate: [
            MsalGuard, PermissionGuard
        ],
    },
    {
        path: 'ewallets',
        redirectTo: '/ewallets/list',
        pathMatch: 'full',
    },
    {
        path: 'ewallets',
        component: EwalletsComponent,
        children: [
            {
                path: 'list',
                component: EwalletListComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionTransactions]
                },
                canActivate: [
                    MsalGuard, PermissionGuard
                ]
            },
            {
                path: 'closure-schedule',
                component: EwalletClosureScheduleComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionTransactions]
                },
                canActivate: [
                    MsalGuard, PermissionGuard
                ]
            }
        ],
        data: {
            allowedPermissions: [PermissionKeys.gestionTransactions]
        },
        canActivate: [
            MsalGuard, PermissionGuard
        ],
    },
    {
        path: 'forgetme',
        component: ForgetMeComponent,
        canActivate: [
            MsalGuard
        ]
    },
    {
        path: 'infrastructure',
        redirectTo: '/infrastructure/office-hour-management',
        pathMatch: 'full',
    },
    {
        path: 'infrastructure',
        component: InfrastructureComponent,
        children: [
            {
                path: 'office-hour-management',
                component: OfficeHourManagementComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionInfrastructure]
                },
                canActivate: [
                    MsalGuard, PermissionGuard
                ]
            },
            {
                path: 'maintenance',
                component: MaintenanceComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionInfrastructure]
                },
                canActivate: [
                    MsalGuard, PermissionGuard
                ]
            },
            {
                path: 'maintenance/equipment/create',
                component: ManageEquipmentComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionInfrastructure]
                },
                canActivate: [
                    MsalGuard, PermissionGuard
                ]
            },
            {
                path: 'maintenance/equipment/:id',
                component: ManageEquipmentComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionInfrastructure]
                },
                canActivate: [
                    MsalGuard, PermissionGuard
                ]
            },
            {
                path: 'interventions',
                component: InterventionsComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionInfrastructure]
                },
                canActivate: [
                    MsalGuard, PermissionGuard
                ]
            },
            {
                path: 'interventions/create',
                component: ManageInterventionComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionInfrastructure]
                },
                canActivate: [
                    MsalGuard, PermissionGuard
                ]
            },
            {
                path: 'interventions/:id',
                component: ManageInterventionComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionInfrastructure]
                },
                canActivate: [
                    MsalGuard, PermissionGuard
                ]
            },
        ],
        data: {
            allowedPermissions: [PermissionKeys.gestionInfrastructure]
        },
        canActivate: [
            MsalGuard, PermissionGuard
        ]
    },
    {
        path: 'cards',
        component: CardsComponent,
        data: {
            allowedPermissions: [PermissionKeys.gestionCartes]
        },
        canActivate: [
            MsalGuard, PermissionGuard
        ]
    },
    {
        path: 'services',
        component: ServicesComponent,
    },
    {
        path: 'services/manage',
        component: ManageServiceComponent,
        data: {
            allowedPermissions: [PermissionKeys.gestionServices]
        },
        canActivate: [
            MsalGuard, PermissionGuard
        ]
    },
    {
        path: 'services/manage/:serviceId',
        component: ManageServiceComponent,
        canActivate: [
            MsalGuard, ServiceEditGuard
        ]
    },
    {
        path: 'services/:serviceId',
        component: ViewServiceComponent
    },
    {
        path: 'services/:serviceId/activate/:measuringPointId',
        component: ViewServiceComponent
    },
    {
        path: 'services/:serviceId/craning-booking/print/:from/:to',
        component: CraningBookingPrintComponent,
        canActivate: [
            MsalGuard
        ]
    },
    {
        path: 'services/:serviceId/craning-booking/print/:craningBookingId',
        component: CraningBookingPrintComponent,
        canActivate: [
            MsalGuard
        ]
    },
    {
        path: 'services/:serviceId/start-charge/:externalChargingStationId/evse/:evseIndex',
        component: ViewServiceComponent
    },
    {
        path: 'services/:serviceId/stop-charge/:chargeProcessId',
        component: ViewServiceComponent
    },
    {
        path: 'statistics',
        component: StatisticsComponent,
        data: {
            allowedRoles: [AppRoles.backoffice]
        },
        canActivate: [
            MsalGuard, RoleGuard
        ]
    },
    {
        path: 'transactions',
        redirectTo: '/transactions/transaction-consumption',
        pathMatch: 'full',

    },
    {
        path: 'transactions',
        component: TransactionsComponent,
        children: [
            {
                path: 'transaction-consumption',
                component: TransactionCommonComponent,
                data: {
                    allowedRoles: [AppRoles.backoffice, AppRoles.customer],
                    transactionType: TransactionType.ServiceConsumption
                },
                canActivate: [
                    MsalGuard, RoleGuard
                ]
            },
            {
                path: 'transaction-credit',
                component: TransactionCommonComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionTransactions],
                    transactionType: TransactionType.EWalletCredit
                },
                canActivate: [
                    MsalGuard, PermissionGuard
                ]
            },
            {
                path: 'transaction-billing',
                component: TransactionBillingComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionTransactions]
                },
                canActivate: [
                    MsalGuard, PermissionGuard
                ]
            },
            {
                path: 'search',
                component: TransactionFullSearchComponent,
                data: {
                    allowedRoles: [AppRoles.superAdmin]
                },
                canActivate: [
                    MsalGuard, RoleGuard
                ]
            }
        ],
        data: {
            allowedRoles: [AppRoles.backoffice, AppRoles.customer]
        },
        canActivate: [
            MsalGuard, RoleGuard
        ]
    },
    {
        path: 'users',
        redirectTo: '/users/customers',
        pathMatch: 'full'
    },
    {
        path: 'users',
        component: UsersComponent,
        children: [
            {
                path: 'customers',
                component: CustomersComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionClients],
                    alternativeRoute: '/users/backoffices',
                    alternativePermissions: [PermissionKeys.gestionBackoffices]
                },
                canActivate: [
                    MsalGuard, PermissionGuard
                ]
            },
            {
                path: 'backoffices',
                component: BackofficesComponent,
                data: {
                    allowedPermissions: [PermissionKeys.gestionBackoffices],
                    alternativeRoute: '/users/customers',
                    alternativePermissions: [PermissionKeys.gestionClients]
                },
                canActivate: [
                    MsalGuard, PermissionGuard
                ]
            }
        ],
        canActivate: [
            MsalGuard
        ]
    },
    {
        path: 'users/backoffices/create',
        component: BackofficeManagementComponent,
        data: {
            allowedPermissions: [PermissionKeys.gestionBackoffices]
        },
        canActivate: [
            MsalGuard, PermissionGuard
        ]
    },
    {
        path: 'users/backoffices/:id',
        component: BackofficeManagementComponent,
        data: {
            allowedPermissions: [PermissionKeys.gestionBackoffices]
        },
        canActivate: [
            MsalGuard, PermissionGuard
        ]
    },
    {
        path: 'users/customers/create',
        component: CustomerManagementComponent,
        data: {
            allowedPermissions: [PermissionKeys.gestionClients]
        },
        canActivate: [
            MsalGuard, PermissionGuard
        ]
    },
    {
        path: 'users/me',
        component: MyProfileComponent,
        canActivate: [
            MsalGuard, RoleGuard
        ]
    },
    {
        path: 'users/customers/:id',
        component: CustomerManagementComponent,
        data: {
            allowedPermissions: [PermissionKeys.gestionClients]
        },
        canActivate: [
            MsalGuard, PermissionGuard
        ]
    },
    {
       // Handle B2C redirection where the path contains a token
       matcher: (url: UrlSegment[]) => {
           return isB2CRedirectionPath(url);
       },
       component: ServicesComponent,
   }
];

const isB2CRedirectionPath = (url: UrlSegment[]) => {
    return url.length === 1 && (url[0].path.startsWith('state') || url[0].path.startsWith('error') || url[0].path.startsWith('code'))
        ? { consumed: url } : null;
}

const isIframe = window !== window.parent && !window.opener;

@NgModule({
    imports: [RouterModule.forRoot(routes, {
    useHash: true,
    initialNavigation: !isIframe ? 'enabledBlocking' : 'disabled',
    scrollPositionRestoration: 'top',
    // Angular 22 fait passer ce defaut de 'emptyOnly' a 'always'
    paramsInheritanceStrategy: 'emptyOnly'
})],
    exports: [RouterModule]
})
export class AppRoutingModule {
}
